import React from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { Picture } from '../html5/Picture';
import { ImageModel, LinkModel, ProductAttributes, ProductTileModel, TrustedHtmlString } from '../../models/globals';
import {
    getColorClass,
    getBgColorClass,
    getDurationText,
    getEnrollmentDateTextForBanner,
} from '../../utilities/ProductUtilities';
import { ProductClassification, ProductType } from '../../models/enums';
import { useIsIE } from '../../hooks/useIsIE';
import DurationIcon from '../../svgs/duration.svg';
import EnrollmentIcon from '../../svgs/enrollment.svg';
import ExternshipIcon from '../../svgs/externship.svg';
import { publishProductClickAnalyticsEvent } from '../../services/analytics';

interface ProductTileProps {
    product: ProductTileModel;
    type: ProductType;
    classification: ProductClassification;
    title: TrustedHtmlString;
    detailPageUrl: LinkModel;
    image: ImageModel;
    defaultImage: ImageModel;
    index: number;
    attributes: ProductAttributes | null;
    typeName: string;
}

const ProductTile: React.FC<ProductTileProps> = ({
    type,
    classification,
    title,
    typeName,
    detailPageUrl,
    image,
    defaultImage,
    attributes,
    index,
    product,
}) => {
    const isIE = useIsIE();
    const renderAttributes = attributes !== null;
    const colorClass = getColorClass({ classification });
    const cardClass = `${renderAttributes ? '' : 'border-0'} ${isIE ? '' : 'h-100'}`.trim();
    const cardBodyClass = `${!renderAttributes ? 'p-0' : 'p-1'}`;

    return (
        <Card className={cardClass}>
            <ProductTileImage
                className={isIE ? 'h-100' : ''}
                classification={classification}
                defaultImage={defaultImage}
                image={image}
                typeName={typeName}
            />
            <Card.Body className={cardBodyClass}>
                <Button
                    aria-label={title}
                    className="pl-0 text-reset text-left text-decoration-none stretched-link"
                    variant="link"
                    href={detailPageUrl.url}
                    data-testid={`product-tile-link-${detailPageUrl.url}`}
                    dangerouslySetInnerHTML={{ __html: title }}
                    onClick={async (event) => {
                        event.preventDefault();
                        try {
                            await publishProductClickAnalyticsEvent(product, index);
                        } catch (e) {
                            console.error(e);
                        } finally {
                            window.location.assign(detailPageUrl.url);
                        }
                    }}
                />
            </Card.Body>
            {attributes !== null && (
                <Card.Footer className="p-1 border-top-0 bg-transparent">
                    {attributes.isExternshipProgram && (
                        <Row>
                            <Col className="pb-1">
                                <ExternshipIcon
                                    aria-label="externship program icon"
                                    className={`${colorClass} size-4`}
                                />
                                <span className="pl-1 small">Clinical Experience</span>
                            </Col>
                        </Row>
                    )}
                    <Row>
                        <Col className="pb-1">
                            <DurationIcon aria-label="duration icon" className={`${colorClass} size-4`} />
                            <span className="pl-1 small">
                                {getDurationText({ ...attributes, classification, type })}
                            </span>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="pb-1">
                            <EnrollmentIcon aria-label="enrollment status icon" className={`${colorClass} size-4`} />
                            <span className="pl-1 small">{getEnrollmentDateTextForBanner(attributes)}</span>
                        </Col>
                    </Row>
                </Card.Footer>
            )}
        </Card>
    );
};

interface ProductTileImageProps {
    classification: ProductClassification;
    className?: string;
    defaultImage: ImageModel;
    image: ImageModel;
    typeName: string;
}

const ProductTileImage: React.FC<ProductTileImageProps> = ({
    classification,
    className = '',
    defaultImage,
    image,
    typeName,
}) => {
    const bgColorClass = getBgColorClass({ classification });

    return (
        <div className={`position-relative product-tile-image ${className}`.trim()}>
            <Picture defaultImage={defaultImage} {...image} />
            <span className={`product-type py-2 px-3 position-absolute text-white ${bgColorClass}`}>{typeName}</span>
        </div>
    );
};

export { ProductTile, ProductTileProps, ProductTileImage };
